import React, {useEffect, useState} from 'react';
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";

import {ToastSeverityType} from "primereact/toast";
import {DashboardReportsApi} from "api/DashboardReportsApi";
import {DashboardInfos} from "api/apiModels";
import {InputSwitch} from "primereact/inputswitch";
import {SelectButton} from "primereact/selectbutton";
import {TemplateLoadingType} from "../product-template-table/utils/enums";
import {IConfigDataConfigLoadingConfig} from "../product-template-table/utils/interfaces";
import {getTemplateLoadingRange} from "../product-template-table/utils/timeUtils";
import {ProgressSpinner} from "primereact/progressspinner";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import {useGetDashboardsInfo} from "api/api-hooks/dashboards-hooks";
import {useGenerateReport, useMarkForRegenerationRange} from "api/api-hooks/reports-hooks";

type GenerateReportDialogType = {
    ids: Array<number>
    showToast(severity: ToastSeverityType, summary: string, detail: string, lifetime?: number): void
    loadingConfig?: IConfigDataConfigLoadingConfig

    close(): void
}

const methods = [
    {label: 'Single', value: false},
    {label: 'Range', value: true}
];

const GenerateNewReport = (props: GenerateReportDialogType) => {
    const [method, setMethod] = useState(false)

    const [dashboard, setDashboard] = useState<DashboardInfos | undefined>(undefined);

    const [fromDate, setFromDate] = useState<Date | undefined>();
    const [toDate, setToDate] = useState<Date | undefined>();

    const [fillMissingSlots, setFillMissingSlots] = useState(false)
    const [generateNearest, setGenerateNearest] = useState(true)

    const {data, isLoading} = useGetDashboardsInfo(props.ids);

    const {mutate: generateNewReport} = useGenerateReport({
        onSuccess: () => {
            props.showToast("success", "", "Report generated successfully")
        },
        onFailure: () => {
            props.showToast("error", "", "Error while generating report")
        },
        onMutate: () => {
            props.showToast("info", "", "Report generation initiated")
            props.close()
        }
    });


    const {mutate: regenerateRange} = useMarkForRegenerationRange({
        onSuccess: () => {
            props.showToast("success", "", "Report range marked successfully")
        },
        onFailure: () => {
            props.showToast("error", "", "Error while marking generation range")
        },
        onMutate: () => {
            props.showToast("info", "", "Report range marking initiated")
            props.close()
        }
    });

    useEffect(() => {
        let {start, end} = getTemplateLoadingRange({
            loadingType: TemplateLoadingType.RANGE,
            loadingConfig: props.loadingConfig
        });

        setFromDate(start || new Date());
        setToDate(end || new Date());
    }, [])

    useEffect(() => {
        if (data && !isLoading && data.length === 1) {
            setDashboard(data[0])
        }
    }, [data, isLoading])

    const onDashboardChange = (e: { value: any }) => {
        setDashboard(e.value);
    }

    const onGenerateClick = (generateOnly?: boolean) => {
        if (!dashboard) {
            props.showToast("warn", "Warning", "No dashboard selected")
            return
        }

        if (generateOnly) {
            generateNewReport({
                dashboardId: dashboard.id,
                referenceTime: toDate?.toISOString() || new Date().toISOString(),
                generateNearest: generateNearest
            })
        } else {
            window.open(DashboardReportsApi.getGenerationAndDownloadUrl({
                dashboardId: dashboard.id,
                referenceTime: toDate?.toISOString() || new Date().toISOString(),
                generateOnly: false,
                generateNearest: generateNearest
            }))

            props.close()
        }

    }

    const onRegenerateClick = () => {
        regenerateRange({
            dashboardId: dashboard!!.id,
            fillMissingSlots: fillMissingSlots,
            from: fromDate?.toISOString() || new Date().toISOString(),
            to: toDate?.toISOString() || new Date().toISOString()
        })
    }

    if (isLoading) {
        return <ProgressSpinner/>
    }

    return (
        <div className={"flex flex-column gap-3"}>
            <div className={"flex gap-2 align-items-center pt-1"}>
                <SelectButton value={method}
                              options={methods}
                              onChange={(e) => setMethod(e.value)}
                />
            </div>
            <div className={"flex flex-column"}>
                <label htmlFor="report"><b>Dashboard</b></label>
                {props.ids.length > 1 ?
                    <Dropdown value={dashboard} options={data} onChange={onDashboardChange}
                              optionLabel="title"
                              placeholder="Select a Dashboard"/>
                    :
                    <>
                        {
                            props.ids.length === 1 &&
                            <InputText value={dashboard?.title || ""} disabled/>
                        }
                    </>
                }
            </div>

            {method ?
                <div className={"flex flex-column gap-1"}>
                    <div style={{fontWeight: "bold", fontSize: "larger", paddingTop: "5px"}}>Reference Time</div>
                    <div className={"flex flex-column"}>
                        <label htmlFor="from"><b>From</b></label>
                        <CalendarWrapper id="from" value={fromDate || new Date()} onChange={setFromDate}/>
                    </div>
                    <div className={"flex flex-column"}>
                        <label htmlFor="to"><b>To</b></label>
                        <CalendarWrapper id="to" value={toDate || new Date()} onChange={setToDate}/>
                    </div>
                </div>
                :
                <div className={"flex flex-column"}>
                    <label htmlFor="referenceName"><b>Reference Time</b></label>
                    <CalendarWrapper id="referenceName" value={toDate || new Date()} onChange={setToDate}/>
                </div>
            }

            {method ?
                <div className={"flex gap-1 align-items-center"}>
                    <label htmlFor="fillMissing"><b>Fill missing slots</b></label>
                    <InputSwitch id={"fillMissing"} checked={fillMissingSlots}
                                 onChange={(e) => setFillMissingSlots(e.value)}/>
                </div>
                :
                <div className={"flex gap-2 align-items-center"}>
                    <label htmlFor="generateNearest"><b>Generate Nearest</b></label>
                    <InputSwitch id={"generateNearest"} checked={generateNearest}
                                 onChange={(e) => setGenerateNearest(e.value)}/>
                </div>
            }

            {method ?
                <Button label={"Generate"} onClick={() => onRegenerateClick()} disabled={!dashboard}/>
                :
                <div className={"flex gap-1"}>
                    <Button label={"Generate"} onClick={() => onGenerateClick(true)} disabled={!dashboard}/>
                    <Button label={"Generate & Download"} onClick={() => onGenerateClick()} disabled={!dashboard}/>
                </div>
            }
        </div>
    );
};

export default GenerateNewReport;